import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { MessageService } from "primeng/api";
import { Ticket } from "src/app/models/ticket";
import { TicketMessage } from "src/app/models/ticket-message";
import { TicketMessagesService } from "src/app/services/ticket-messages.service";
import { TicketsService } from "src/app/services/tickets.service";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { TagModule } from "primeng/tag";
import { CardModule } from "primeng/card";
import { TextareaModule } from "primeng/textarea";
import { ToastModule } from "primeng/toast";
import { SelectModule } from "primeng/select";
import { TooltipModule } from "primeng/tooltip";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { MessageModule } from "primeng/message";

@Component({
  standalone: true,
  selector: "app-ticket-detail",
  templateUrl: "./ticket-detail.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    TagModule,
    CardModule,
    TextareaModule,
    ToastModule,
    SelectModule,
    TooltipModule,
    ToggleSwitchModule,
    MessageModule,
  ],
})
export class TicketDetailComponent implements OnInit, OnDestroy {
  ticket: Ticket | null = null;
  conversation: TicketMessage[] = [];
  replyMessage = "";
  ticketStatus = "";
  savingReply = false;
  loading = true;
  hasCustomerUpdate = false;
  error: string | null = null;
  currentUser: any;
  isInternalNote = false;

  private lastCustomerMessageSnapshot = "";
  private refreshTimer: any = null;

  statusOptions = [
    { label: "Open", value: "open" },
    { label: "In Progress", value: "in_progress" },
    { label: "Closed", value: "closed" },
  ];

  priorityOptions = [
    { label: "Low", value: "low" },
    { label: "Normal", value: "normal" },
    { label: "High", value: "high" },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ticketsService: TicketsService,
    private ticketMessagesService: TicketMessagesService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
  ) {
    try {
      this.currentUser = JSON.parse(
        localStorage.getItem("currentUser") || "null",
      );
    } catch {
      this.currentUser = null;
    }
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get("id");
    if (id) {
      this.loadTicket(+id);
    }
  }

  loadTicket(id: number): void {
    this.loading = true;
    this.ticketsService.getId(id).subscribe({
      next: (data: any) => {
        this.ticket = data as Ticket;
        this.ticketStatus = this.ticket.status || "open";
        this.loading = false;
        this.cdr.markForCheck();
        this.loadConversation();
        this.startAutoRefresh();
      },
      error: () => {
        this.error = "Unable to load ticket.";
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  loadConversation(showToast = false): void {
    if (!this.ticket?.id) return;

    const params = {
      ticket_id: this.ticket.id,
      _start: 0,
      _limit: 200,
      _sort: "created_at",
      _order: "asc",
    };

    this.ticketMessagesService.list(params).subscribe({
      next: (rows) => {
        this.conversation = rows || [];

        const customerMessages = this.conversation.filter(
          (m) => m.sender_type === "customer",
        );
        const last = customerMessages[customerMessages.length - 1];
        const snapshot = last ? `${last.id}|${last.created_at}` : "";

        this.hasCustomerUpdate =
          !!this.lastCustomerMessageSnapshot &&
          snapshot !== this.lastCustomerMessageSnapshot;

        this.lastCustomerMessageSnapshot = snapshot;
        this.cdr.markForCheck();

        if (showToast) {
          this.messageService.add({
            key: "ticketToast",
            severity: this.hasCustomerUpdate ? "warn" : "info",
            summary: "Refreshed",
            detail: this.hasCustomerUpdate
              ? "Customer sent a new message"
              : "No new messages",
          });
        }
      },
      error: () => {
        if (showToast) {
          this.messageService.add({
            key: "ticketToast",
            severity: "error",
            summary: "Error",
            detail: "Unable to load conversation",
          });
        }
      },
    });
  }

  saveReply(): void {
    if (!this.ticket?.id || this.savingReply || !this.replyMessage.trim()) {
      return;
    }

    this.savingReply = true;

    const replyFormData = new FormData();
    replyFormData.append("ticket_id", String(this.ticket.id));
    replyFormData.append("sender_type", "admin");
    replyFormData.append(
      "sender_user_id",
      String(this.currentUser?.user?.id || this.currentUser?.user_id || ""),
    );
    replyFormData.append("message", this.replyMessage.trim());
    replyFormData.append("is_internal_note", this.isInternalNote ? "1" : "0");

    this.ticketMessagesService.create(replyFormData).subscribe({
      next: () => {
        const ticketFormData = new FormData();
        ticketFormData.append("status", this.ticketStatus);

        if (this.ticketStatus === "closed") {
          ticketFormData.append("closed_at", new Date().toISOString());
        } else {
          ticketFormData.append("closed_at", "");
        }

        this.ticketsService.update(ticketFormData, +this.ticket.id).subscribe({
          next: () => {
            this.messageService.add({
              key: "ticketToast",
              severity: "success",
              summary: "Sent",
              detail: this.isInternalNote
                ? "Internal note saved"
                : "Reply sent successfully",
            });

            this.replyMessage = "";
            this.isInternalNote = false;
            this.hasCustomerUpdate = false;
            this.savingReply = false;
            this.ticket.status = this.ticketStatus;
            this.cdr.markForCheck();
            this.loadConversation();
          },
          error: () => {
            this.savingReply = false;
            this.cdr.markForCheck();
            this.messageService.add({
              key: "ticketToast",
              severity: "warn",
              summary: "Warning",
              detail: "Reply saved but status update failed",
            });
          },
        });
      },
      error: () => {
        this.savingReply = false;
        this.cdr.markForCheck();
        this.messageService.add({
          key: "ticketToast",
          severity: "error",
          summary: "Error",
          detail: "Unable to send reply",
        });
      },
    });
  }

  goBack(): void {
    this.router.navigate(["/admin/tickets"]);
  }

  getStatusSeverity(status: string): string {
    const n = (status || "").toLowerCase();
    if (n === "closed") return "success";
    if (n === "in_progress") return "warning";
    return "info";
  }

  getPrioritySeverity(priority: string): string {
    const n = (priority || "").toLowerCase();
    if (n === "high") return "danger";
    if (n === "low") return "secondary";
    return "warning";
  }

  formatDate(dateValue: any): string {
    if (!dateValue) return "";
    const d = new Date(dateValue);
    return isNaN(d.getTime()) ? "" : d.toLocaleString();
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.refreshTimer = setInterval(() => {
      this.loadConversation(false);
    }, 20000);
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }
}
